/**
 * Staleness check for `trialing` profiles.
 *
 * Kept as its own pure module (no supabase import) so it can be unit-tested
 * directly rather than re-implemented in the test file — the pattern the
 * existing `userAccessService` tests were forced into.
 */

/**
 * How long after a trial's period end we keep honouring `trialing`.
 *
 * The conversion webhook lands minutes-to-hours after the trial ends, and the
 * Neon→profiles sync is the slow path behind it. Walling a member during that
 * window — someone who just converted and paid — is a worse failure than
 * honouring a few stale days, so the check deliberately waits. 72h matches the
 * store grace/lag allowance the funnel's own conversion metric uses.
 */
export const TRIAL_PERIOD_END_GRACE_HOURS = 72;

/**
 * True when a `trialing` status is provably left-over state rather than access.
 *
 * A trial that has ended either converted (status → `active`) or lapsed
 * (→ `cancelled`); in both cases the store has moved on and a profile still
 * reading `trialing` is what a missed webhook or a lagging sync left behind.
 * ~881 profiles have sat in exactly this state.
 *
 * Conservative on purpose, in four ways:
 *  - Only `trialing`. An `active` subscription's period end is the end of the
 *    CURRENT period and renews, so a past value there is completely normal —
 *    applying this to `active` would wall the entire paying base.
 *  - Only when a period end is actually known. Absent/null ⇒ not stale, so a
 *    caller that cannot supply the column can never wall anyone.
 *  - Unparseable dates are treated as unknown, not as expired.
 *  - A grace window before the date counts as past (see above).
 *
 * Callers must treat `true` as "stop short-circuiting to full", NOT as "deny" —
 * enrollment and course-access checks still decide, and a trial buyer enrolled
 * in a live entry challenge keeps full access on that basis.
 */
export function isStaleTrial(
  subscriptionStatus: string | null | undefined,
  periodEnd: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (subscriptionStatus !== "trialing") return false;
  if (!periodEnd) return false;

  const end = new Date(periodEnd);
  if (Number.isNaN(end.getTime())) return false;

  return end.getTime() + TRIAL_PERIOD_END_GRACE_HOURS * 60 * 60 * 1000 < now.getTime();
}
